import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SchoolLevel } from '@jeraisonne/db';
import { foldText, schoolSubtitle, type SchoolHit, type SchoolKind } from '@jeraisonne/db/schools';
import { Alert, AlertDescription, Button, Input, cn } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';

// Colonnes et RPC de l'annuaire (migration 0705) postérieures aux types stale
// (cf. CLAUDE.md) → client générique + typage à la main.
const db = supabase as unknown as SupabaseClient;

/** L'établissement choisi, tel que le profil le lit (embed `school:schools(…)`). */
export interface CurrentSchool {
  id: string;
  name: string;
  city: string | null;
  departement: string | null;
  kind: SchoolKind;
  sector: 'public' | 'prive' | null;
}

const COLLEGE_LEVELS: ReadonlySet<string> = new Set(['6e', '5e', '4e', '3e']);

/** Filtre proposé d'abord : collèges pour 6e → 3e, lycées pour 2nde → prépa. */
export function defaultSchoolKind(level: SchoolLevel | null | undefined): SchoolKind | null {
  if (!level) return null;
  return COLLEGE_LEVELS.has(level) ? 'college' : 'lycee';
}

const KIND_FILTERS: { value: SchoolKind | null; label: string }[] = [
  { value: 'college', label: 'Collèges' },
  { value: 'lycee', label: 'Lycées' },
  { value: null, label: 'Tous' },
];

/** Saisie retardée (on ne cherche pas à chaque lettre). */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Établissement de l'élève (FACULTATIF), choisi dans l'annuaire officiel des
 * collèges et lycées (migration 0705, ~14 800 établissements, DOM et
 * collectivités d'outre-mer compris) depuis « Mon profil ». Recherche par
 * nom, ville ou code postal (RPC `search_schools`). Le choix recopie le
 * département et la région de l'établissement (trigger SQL) et alimente les
 * classements par établissement, ville, département.
 */
export function StudentSchoolControl({
  userId,
  currentLevel,
  currentSchool,
}: {
  userId: string;
  currentLevel: SchoolLevel | null;
  currentSchool: CurrentSchool | null;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<SchoolKind | null>(defaultSchoolKind(currentLevel));
  const query = useDebounced(text, 250);
  const folded = foldText(query);
  const panel = useRef<HTMLDivElement>(null);

  // le panneau s'ouvre en haut de l'écran : les résultats ne passent pas sous la barre du bas ni le clavier
  useEffect(() => {
    if (editing) panel.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [editing]);

  const search = useQuery({
    queryKey: ['school-search', folded, kind],
    enabled: editing && folded.length >= 2,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<SchoolHit[]> => {
      const { data, error } = await db.rpc('search_schools', {
        p_query: query,
        p_kind: kind,
        p_limit: 20,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as SchoolHit[];
    },
  });

  const save = useMutation({
    mutationFn: async (schoolId: string | null) => {
      const { error } = await db
        .from('student_profiles')
        .update({ school_id: schoolId })
        .eq('id', userId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-profile', userId] });
      setEditing(false);
      setText('');
    },
  });

  return (
    <div className="space-y-3" data-testid="student-school-control">
      <div className="flex items-center justify-between gap-3">
        <span className="text-ink-600 text-sm">Établissement</span>
        <div className="flex items-center gap-2">
          {!currentSchool && (
            <span className="text-ink-900 text-sm font-semibold" data-testid="student-school-name">
              Non renseigné
            </span>
          )}
          {!editing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setKind(
                  currentSchool?.kind === 'college' || currentSchool?.kind === 'lycee'
                    ? currentSchool.kind
                    : currentSchool
                      ? null
                      : defaultSchoolKind(currentLevel),
                );
                setEditing(true);
              }}
            >
              {currentSchool ? 'Modifier' : 'Choisir'}
            </Button>
          )}
        </div>
      </div>

      {currentSchool && (
        <div className="bg-ink-50 rounded-xl px-3 py-2">
          <p className="text-ink-900 text-sm font-semibold" data-testid="student-school-name">
            {currentSchool.name}
          </p>
          <p className="text-ink-500 text-xs">{schoolSubtitle(currentSchool)}</p>
        </div>
      )}

      {!editing && !currentSchool && (
        <p className="text-ink-400 text-xs italic">
          Facultatif — sert aux classements par établissement, ville et département.
        </p>
      )}

      {editing && (
        <div
          ref={panel}
          className="border-ink-100 scroll-mt-4 space-y-3 rounded-xl border bg-white p-3"
        >
          <div className="flex gap-2" role="group" aria-label="Type d’établissement">
            {KIND_FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                aria-pressed={kind === f.value}
                onClick={() => setKind(f.value)}
                className={cn(
                  'min-h-11 flex-1 rounded-full border px-3 text-sm font-medium transition',
                  kind === f.value
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-ink-200 text-ink-600 bg-white',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <Input
            type="search"
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Nom, ville ou code postal"
            aria-label="Chercher ton établissement"
            className="min-h-11"
          />

          <div className="max-h-80 space-y-1 overflow-y-auto" data-testid="school-results">
            {folded.length < 2 && (
              <p className="text-ink-400 px-1 text-xs">
                Exemples : « Jean Moulin Béziers », « lycée Lapérouse », « 34500 ».
              </p>
            )}
            {folded.length >= 2 && search.isFetching && !search.data && (
              <p className="text-ink-400 px-1 text-xs">Recherche…</p>
            )}
            {folded.length >= 2 && search.data?.length === 0 && (
              <p className="text-ink-500 px-1 text-sm">
                Aucun établissement trouvé. Essaie la ville ou le code postal
                {kind ? ', ou « Tous »' : ''}.
              </p>
            )}
            {folded.length >= 2 &&
              search.data?.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  disabled={save.isPending}
                  onClick={() => save.mutate(s.id)}
                  className={cn(
                    'hover:bg-ink-50 flex min-h-11 w-full flex-col items-start rounded-lg px-2 py-2 text-left transition',
                    currentSchool?.id === s.id && 'bg-primary-50',
                  )}
                >
                  <span className="text-ink-900 text-sm font-semibold">{s.name}</span>
                  <span className="text-ink-500 text-xs">{schoolSubtitle(s)}</span>
                </button>
              ))}
          </div>

          {(save.isError || search.isError) && (
            <Alert variant="error">
              <AlertDescription>
                {((save.error ?? search.error) as Error)?.message ?? 'Une erreur est survenue.'}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {currentSchool && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full sm:w-auto"
                disabled={save.isPending}
                onClick={() => save.mutate(null)}
              >
                Retirer mon établissement
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              disabled={save.isPending}
              onClick={() => {
                setEditing(false);
                setText('');
                save.reset();
              }}
            >
              Annuler
            </Button>
          </div>
          <p className="text-ink-400 text-xs">
            Ton département et ta région suivent l’établissement choisi. Source : annuaire de
            l’Éducation nationale.
          </p>
        </div>
      )}
    </div>
  );
}
